import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowRight, TrendingUp, TrendingDown, Plus, Minus, GitCompareArrows, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { InfoTip } from "@/components/app/InfoTip";

const TABS = [
  { key: "rising",   label: "Rising",   icon: TrendingUp,   tone: "emerald" },
  { key: "declining",label: "Declining",icon: TrendingDown, tone: "rose" },
  { key: "new_keywords",     label: "New",     icon: Plus,  tone: "violet" },
  { key: "dropped_keywords", label: "Dropped", icon: Minus, tone: "slate" },
];

export default function CompareWeeks() {
  const { selectedBrandId } = useBrands();
  const [uploads, setUploads] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("rising");

  // Auto-pick the most recent two processed uploads.
  useEffect(() => {
    if (!selectedBrandId) return;
    api.get(`/uploads?brand_id=${selectedBrandId}`).then((r) => {
      const items = (r.data || [])
        .filter((u) => u.upload_status === "Processed")
        .sort((x, y) => (y.week_end_date || "").localeCompare(x.week_end_date || ""));
      setUploads(items);
      if (items[1]) { setA(items[1].id); setB(items[0].id); }
    });
  }, [selectedBrandId]);

  const run = async () => {
    if (!a || !b) return toast.error("Pick both weeks");
    if (a === b) return toast.error("Pick two different weeks");
    setLoading(true);
    try {
      const { data } = await api.get(
        `/dashboard/compare-weeks?brand_id=${selectedBrandId}&upload_a=${a}&upload_b=${b}`,
      );
      setData(data);
    } catch (e) { toast.error(extractError(e, "Failed to compare")); }
    finally { setLoading(false); }
  };

  const labelFor = (id) => {
    const u = uploads.find((x) => x.id === id);
    return u ? `${u.week_start_date} → ${u.week_end_date}` : id?.slice(0, 8);
  };

  const tabRows = useMemo(() => (data ? data[tab] || [] : []), [data, tab]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Trend Analysis</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Compare weeks</h1>
        <p className="mt-1 text-sm text-slate-500">Diff any two processed weeks for this brand — see what's rising, declining, new, and resolved.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-wrap items-end gap-3" data-testid="compare-controls">
        <Picker label="Earlier week (A)" value={a} onChange={setA} uploads={uploads} testid="compare-week-a" />
        <ArrowRight className="h-5 w-5 text-slate-400 mb-2.5" />
        <Picker label="Later week (B)" value={b} onChange={setB} uploads={uploads} testid="compare-week-b" />
        <Button data-testid="compare-run-btn" onClick={run} disabled={loading || !a || !b} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg gap-1.5">
          <GitCompareArrows className="h-4 w-4" /> {loading ? "Comparing…" : "Compare"}
        </Button>
      </div>

      {!data && !loading && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-sm text-slate-500">
          {uploads.length < 2
            ? "Need at least two processed uploads to compare. Upload more CSVs."
            : "Pick two weeks above and click Compare."}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="compare-kpis">
            <Kpi label="Common terms" value={data.kpis.common_terms} />
            <Kpi label="New in B" value={data.kpis.new_in_b} tone="violet" term="NEW_KW" />
            <Kpi label="Dropped" value={data.kpis.dropped_from_a} tone="slate" />
            <Kpi label="Rising" value={data.kpis.rising} tone="emerald" term="RISING" />
            <Kpi label="Declining" value={data.kpis.declining} tone="rose" term="DECLINING" />
            <Kpi label="Δ Avg Opp" value={signed(data.kpis.avg_opportunity_change)} tone={data.kpis.avg_opportunity_change >= 0 ? "emerald" : "rose"} term="OPPORTUNITY" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{labelFor(a)}</span>
                <ArrowRight className="inline h-3 w-3 mx-2 text-slate-400" />
                <span className="font-semibold text-slate-700">{labelFor(b)}</span>
              </div>
              <div className="flex items-center gap-1" data-testid="compare-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`compare-tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium border transition-colors ${
                      tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:bg-slate-50 border-slate-200"
                    }`}
                  >
                    <t.icon className="h-3.5 w-3.5" />
                    {t.label} <span className="opacity-60">{(data[t.key] || []).length}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left">Search term</th>
                    <th className="px-4 py-3 text-left">Cluster</th>
                    <th className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1">Rank A <InfoTip term="RANK" /></span></th>
                    <th className="px-4 py-3 text-right">Rank B</th>
                    <th className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1">Δ Rank <InfoTip term="RANK_CHANGE" /></span></th>
                    <th className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1">Opp A <InfoTip term="OPPORTUNITY" /></span></th>
                    <th className="px-4 py-3 text-right">Opp B</th>
                    <th className="px-4 py-3 text-right">Δ Opp</th>
                  </tr>
                </thead>
                <tbody>
                  {tabRows.map((m, i) => (
                    <tr key={`${tab}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`compare-row-${tab}-${i}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{m.search_term}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{m.cluster || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{m.rank_a ?? "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{m.rank_b ?? "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        {m.rank_change == null ? "—" : (
                          <span className={m.rank_change < 0 ? "text-emerald-700" : m.rank_change > 0 ? "text-rose-700" : "text-slate-500"}>
                            {m.rank_change < 0 ? "▲" : "▼"} {Math.abs(m.rank_change)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{m.opportunity_a}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{m.opportunity_b}</td>
                      <td className="px-4 py-3 text-right font-mono text-xs">
                        <span className={m.opportunity_change > 0 ? "text-emerald-700" : m.opportunity_change < 0 ? "text-rose-700" : "text-slate-500"}>
                          {signed(m.opportunity_change)}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!tabRows.length && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">No movement in this category.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="compare-clusters">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-base font-semibold tracking-tight text-slate-900">Cluster shifts</h3>
                <p className="text-xs text-slate-500 mt-0.5">Where opportunity is moving fastest.</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Cluster</th>
                    <th className="px-4 py-2.5 text-right">Δ Avg Opp</th>
                    <th className="px-4 py-2.5 text-right">A → B</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.cluster_summary || []).map((c) => (
                    <tr key={c.cluster} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 font-medium text-slate-900">{c.cluster}</td>
                      <td className={`px-4 py-2.5 text-right font-mono text-xs ${c.avg_opp_change >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{signed(c.avg_opp_change)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{c.avg_opp_a} → {c.avg_opp_b}</td>
                    </tr>
                  ))}
                  {!(data.cluster_summary || []).length && <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">No cluster activity</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="compare-gaps">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-600" />
                <h3 className="text-base font-semibold tracking-tight text-slate-900">Product-gap delta</h3>
              </div>
              <div className="px-5 py-4 grid grid-cols-3 gap-3 text-sm">
                <GapList tone="violet" label="New gaps" items={data.gaps?.new || []} count={data.gaps?.counts?.new} />
                <GapList tone="emerald" label="Resolved" items={data.gaps?.resolved || []} count={data.gaps?.counts?.resolved} />
                <GapList tone="slate" label="Persistent" items={data.gaps?.persistent || []} count={data.gaps?.counts?.persistent} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Picker({ label, value, onChange, uploads, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="w-[260px]"><SelectValue placeholder="Pick a week" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {uploads.map((u) => (
            <SelectItem key={u.id} value={u.id}>{u.week_start_date} → {u.week_end_date}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Kpi({ label, value, tone = "slate", term }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-900",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-800",
    rose: "border-rose-200 bg-rose-50/40 text-rose-800",
    violet: "border-violet-200 bg-violet-50/40 text-violet-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
        {label}
        {term ? <InfoTip term={term} /> : null}
      </p>
      <p className="mt-1 text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

function GapList({ tone, label, items, count }) {
  const tones = {
    violet: "text-violet-700",
    emerald: "text-emerald-700",
    slate: "text-slate-700",
  };
  return (
    <div>
      <div className={`text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>
        {label} <span className="opacity-70">({count ?? items.length})</span>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-slate-700 max-h-40 overflow-y-auto">
        {items.length ? items.slice(0, 12).map((kw) => <li key={kw} className="truncate">• {kw}</li>) : <li className="text-slate-400">None</li>}
      </ul>
    </div>
  );
}

function signed(n) {
  if (n == null) return "—";
  const v = Number(n).toFixed(1);
  return n > 0 ? `+${v}` : `${v}`;
}
